import { LayoutModule } from '@angular/cdk/layout';
import { PlatformModule } from '@angular/cdk/platform';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
/**
 * For mobile and tablet, reset ranges
 */
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

import { ViAlertComponent } from './components/alert/alert.component';
import { ViBadgeComponent } from './components/badge/badge.component';
import { ViBoxComponent } from './components/box/box.component';
import { ViBreadcrumbsComponent } from './components/breadcrumbs/breadcrumbs.component';
import { ViCardComponent } from './components/card/card.component';
import { ViDropdownAutocompleteComponent } from './components/dropdown-autocomplete/dropdown-autocomplete.component';
import { ViDropdownComponent } from './components/dropdown/dropdown.component';
import { ViToDropdownModelPipe } from './components/dropdown/to-dropdown-model.pipe';
import { ViEmptyComponent } from './components/empty/empty.component';
import { ViGraphComponent } from './components/graph/graph.component';
import { ViIconButtonComponent } from './components/icon-button/icon-button.component';
import { ViIconComponent } from './components/icon/icon.component';
import { ViInfoPanelComponent } from './components/info-panel/info-panel.component';
import { ViInputComponent } from './components/input/input.component';
import { ViItemComponent } from './components/item/item.component';
import { ViJumbotronComponent } from './components/jumbotron/jumbotron.component';
import { ViLoaderComponent } from './components/loader/loader.component';
import { ViLogoComponent } from './components/logo/logo.component';
import { ViMessageComponent } from './components/message/message.component';
import { ViNoResultsComponent } from './components/no-results/no-results.component';
import { ViNotificationBoxComponent } from './components/notification-box/notification-box.component';
import { ViOverlayProgressBarComponent } from './components/overlay-progress-bar/overlay-progress-bar.component';
import { ViPanelComponent } from './components/panel/panel.component';
import { ViProgramItemComponent } from './components/program-item/program-item.component';
import { ViProgressBarComponent } from './components/progress-bar/progress-bar.component';
import { ViRadioButtonComponent } from './components/radio-button/radio-button.component';
import { ViTabsComponent } from './components/tabs/tabs.component';
import { ViTextComponent } from './components/text/text.component';
import { ViToastComponent } from './components/toast/toast.component';
import { ViToastService } from './components/toast/toast.service';
import { ViUserAvatarComponent } from './components/user-avatar/user-avatar.component';
import { ViUserCardComponent } from './components/user-card/user-card.component';
import { ViButtonDirective } from './directives/button/button.directive';
import { ViFeatureGridDirective } from './directives/feature-grid/feature-grid.directive';
import { ViConfirmDialogService } from './directives/confirm/confirm-dialog.service';
import { ViConfirmDialogComponent } from './directives/confirm/confirm-dialog/confirm-dialog.component';
import { ViConfirmDirective } from './directives/confirm/confirm.directive';
import { ViStepperNext, ViStepperPrevious } from './directives/stepper/stepper.directive';
import { ViAsideComponent } from './layouts/aside/aside.component';
import { ViBodyComponent } from './layouts/body/body.component';
import { ViContentComponent } from './layouts/content/content.component';
import { ViFooterComponent } from './layouts/footer/footer.component';
import { ViHeadComponent } from './layouts/head/head.component';
import { ViHeaderComponent } from './layouts/header/header.component';
import { ViPageComponent } from './layouts/page/page.component';
import { ViSidenavButtonComponent } from './layouts/sidenav-button/sidenav-button.component';
import { ViSidenavPanelComponent } from './layouts/sidenav-panel/sidenav-panel.component';
import { ViSidenavComponent } from './layouts/sidenav/sidenav.component';
import { ViToolbarComponent } from './layouts/toolbar/toolbar.component';
import { ViPagePipe } from './pipes/page.pipe';
import { ViTrustHtmlPipe } from './pipes/trust-html.pipe';
import { ViAppService } from './services/app.service';
import { ViLogger } from './services/logger.service';
import { ViEllipsisDirective } from './directives/ellipsis/ellipsis.directive';
import { ViDropdownAutocompleteMultiselectComponent } from './components/dropdown-autocomplete-multiselect/dropdown-autocomplete-multiselect.component';
import { FieldTextContentTooltipDirective } from './directives/tooltip/field-text-content-tooltip.directive';
import { BackButtonComponent } from './components/back-button/back-button.component';
import { ViNotificationComponent } from '@vi-iot/ui/components/notification/notification.component';
import { ViBorderBottomDirective } from './directives/border-bottom/border-bottom.directive';
import { ViContentItemComponent } from './components/content-item/content-item.component';
import { ViCollapsableUiComponent } from './components/collapsable-ui/collapsable-ui.component';
import { ViProgressBarDetermineComponent } from './components/progress-bar-determine/progress-bar-determine.component';

// Layouts

// Components

// Services

// Pipes

// Directives

export const MATERIAL_MODULES = [
  PlatformModule,
  LayoutModule,

  MatAutocompleteModule,
  MatButtonModule,
  MatButtonToggleModule,
  MatBadgeModule,
  MatCardModule,
  MatCheckboxModule,
  MatChipsModule,
  MatDatepickerModule,
  MatDialogModule,
  MatExpansionModule,
  MatGridListModule,
  MatIconModule,
  MatInputModule,
  MatListModule,
  MatMenuModule,
  MatNativeDateModule,
  MatPaginatorModule,
  MatProgressBarModule,
  MatProgressSpinnerModule,
  MatRadioModule,
  MatRippleModule,
  MatSelectModule,
  MatSidenavModule,
  MatSliderModule,
  MatSlideToggleModule,
  MatSnackBarModule,
  MatSortModule,
  MatTableModule,
  MatTabsModule,
  MatToolbarModule,
  MatTooltipModule,
  MatStepperModule,
  MatFormFieldModule,
  ScrollingModule,
];

export const LAYOUTS = [ViSidenavPanelComponent, ViSidenavButtonComponent, ViAsideComponent];

export const COMPONENTS = [
  ViProgressBarComponent,
  ViProgressBarDetermineComponent,
  ViUserCardComponent,
  ViBoxComponent,
  ViGraphComponent,
  ViBadgeComponent,
  ViTextComponent,
  ViProgramItemComponent,
  ViInputComponent,
  ViDropdownComponent,
  ViDropdownAutocompleteComponent,
  ViDropdownAutocompleteMultiselectComponent,
  ViLogoComponent,
  ViJumbotronComponent,
  ViNotificationBoxComponent,
  ViAlertComponent,
  ViOverlayProgressBarComponent,
  ViRadioButtonComponent,
  ViMessageComponent,
  ViEmptyComponent,
  ViNoResultsComponent,
  BackButtonComponent,

  // from Services
  ViToastComponent,

  // Pipes
  ViPagePipe,
  ViToDropdownModelPipe,

  // Directives
  ViStepperNext,
  ViStepperPrevious,
  ViConfirmDialogComponent,
  ViButtonDirective,
  ViFeatureGridDirective,
  ViBorderBottomDirective,
];

export const STANDALONE = [
  ViHeadComponent,
  ViBodyComponent,
  ViFooterComponent,
  ViUserAvatarComponent,
  ViSidenavComponent,
  ViPageComponent,
  ViContentComponent,
  ViLoaderComponent,
  ViInfoPanelComponent,
  ViConfirmDirective,
  ViTrustHtmlPipe,
  FieldTextContentTooltipDirective,
  ViNotificationComponent,
  ViIconButtonComponent,
  ViToolbarComponent,
  ViBreadcrumbsComponent,
  ViHeaderComponent,
  ViPanelComponent,
  ViItemComponent,
  ViCardComponent,
  ViIconComponent,
  ViCardComponent,
  ViTabsComponent,
  ViEllipsisDirective,
  ViContentItemComponent,
  ViCollapsableUiComponent,
];

@NgModule({
  imports: [
    CommonModule,
    FormsModule.withConfig({
      callSetDisabledState: 'whenDisabledForLegacyCode',
    }),
    ReactiveFormsModule.withConfig({
      callSetDisabledState: 'whenDisabledForLegacyCode',
    }),
    RouterModule,
    ...MATERIAL_MODULES,
    ...STANDALONE,
    TranslateModule,
  ],
  exports: [
    PlatformModule,
    LayoutModule,
    FormsModule,
    ReactiveFormsModule,
    ...MATERIAL_MODULES,
    ...LAYOUTS,
    ...COMPONENTS,
    ...STANDALONE,
  ],
  declarations: [...LAYOUTS, ...COMPONENTS],
  providers: [ViLogger, ViConfirmDialogService, ViToastService, ViAppService],
})
export class ViessmannUIModule {}
